"use client";
//TextElement  but with onClick onKeyPress, hence "use client"
import React from "react";

import DOMPurify from "dompurify";

const validElements = ["h1", "h2", "h3", "h4", "h5", "h6", "p", "span", "button"] as const;
const invalidElements = validElements.filter((el) => !["span", "button"].includes(el));

const showWarning =
  typeof window === "object" &&
  (window.location.hostname.includes("localhost") || window.location.hostname.includes("staging"));

type ValidElement = (typeof validElements)[number];

interface TextElementProps {
  text?: string;
  element?: ValidElement;
  onClick?: React.MouseEventHandler<HTMLElement>;
  onKeyPress?: React.KeyboardEventHandler<HTMLElement>;
  className?: string;
  styles?: React.CSSProperties;
  sanitize?: boolean;
  children?: React.ReactNode;
}

export const TextElement: React.FC<TextElementProps> = ({
  text = "",
  element = "p",
  onClick = () => {},
  onKeyPress = () => {},
  className = "",
  styles,
  sanitize = false,
  children,
}) => {
  const Element = validElements.includes(element) ? element : "p";

  // Replace disallowed tags inside <p>
  if (Element === "p" && typeof text === "string") {
    invalidElements.forEach((el) => {
      if (text.includes(`<${el}`)) {
        if (showWarning) {
          //console.warn(`TextElement: <${el.toUpperCase()}> in <P> replaced with <SPAN>`, text);
        }
        const openTag = new RegExp(`(<${el})`, "igm");
        const closeTag = new RegExp(`(</${el})`, "igm");
        text = text.replace(openTag, "<span").replace(closeTag, "</span");
      }
    });
  }

  const commonProps = {
    className: `textelement ${className}`.trim(),
    style: styles,
    onClick,
    onKeyPress,
  };

  if (React.isValidElement(text)) {
    return (
      <Element {...commonProps}>
        {sanitize ? DOMPurify.sanitize(text, { ALLOWED_TAGS: [] }) : text}
      </Element>
    );
  }

  if (children) {
    return <Element {...commonProps}>{children}</Element>;
  }

  return (
    <Element
      {...commonProps}
      dangerouslySetInnerHTML={{
        __html: sanitize
          ? DOMPurify.sanitize(text as string, { ALLOWED_TAGS: [] })
          : (text as string),
      }}
    />
  );
};
